import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { Share2Icon } from 'lucide-react';

import usePage, { PageProvider } from '@dashboard/hooks/usePage';

import { CheckoutShareButton } from '@dashboard/components/CheckoutShare';
import { Skeleton } from '@dashboard/components/ui/skeleton';

import CheckoutOverview from './scenes/CheckoutOverview';
import EventOverview from './scenes/EventOverview';
import EventStaff from './scenes/EventStaff/EventStaff';
import FormOverview from './scenes/FormOverview';
import PageCustomers from './scenes/PageCustomers';
import PagePayments from './scenes/PagePayments';
import PageSubmissions from './scenes/PageSubmissions';
import PageSubscriptions from './scenes/PageSubscriptions';
import PageBookings from './scenes/PageBookings';
import PageGuests from './scenes/PageGuests';

import { getOverviewScreens } from '@dashboard/scenes/PageEdit/editorNavigation';
import {
  PAGE_CONTENT_COLUMN_END,
  PAGE_CONTENT_COLUMN_START,
} from '@dashboard/layouts/NavigationParts';
import EditorSidebar, {
  EditorSearchButton,
  EditorTopBarStart,
  useTopBarStyle,
} from '@dashboard/scenes/PageEdit/components/EditorSidebar';
import SummarySkeleton, { SummarySidebarSkeleton } from './components/SummarySkeleton';
import PageDetail from './components/PageDetail';
import { readNavSnapshot } from '@dashboard/lib/pageNavSnapshot';
import ListScreenSkeleton from './components/ListScreenSkeleton';
import { OrderPageSkeleton, orderTabSkeleton } from '@dashboard/components/OrderPage';
import { PageSummaryRangeControls, PageSummaryRangeProvider } from './components/PageSummaryRange';
import { DetailBaseContext, PageScopeContext } from '@dashboard/lib/detailLinks';
import CustomerDetail from '@dashboard/scenes/CustomerDetail';
import AttendeeDetail from '@dashboard/scenes/AttendeeDetail/AttendeeDetail';
import PaymentsChargesDetailOverview from '@dashboard/scenes/PaymentsChargesDetail/PaymentsChargesDetailOverview';
import PaymentsSubscriptionsDetailOverview from '@dashboard/scenes/PaymentsSubscriptionsDetail/PaymentsSubscriptionsDetailOverview';
import PaymentsBookingsDetailOverview from '@dashboard/scenes/PaymentsBookingsDetail';
import SubmissionsDetailOverview from '@dashboard/scenes/SubmissionsDetail/SubmissionsDetailOverview';

const SCREEN_COMPONENTS = {
  checkout: {
    overview: CheckoutOverview,
    payments: PagePayments,
    subscriptions: PageSubscriptions,
    customers: PageCustomers,
  },
  event: {
    overview: EventOverview,
    bookings: PageBookings,
    attendees: PageGuests,
  },
  form: {
    overview: FormOverview,
    submissions: PageSubmissions,
    leads: PageCustomers,
  },
};

const CUSTOMER_SCREENS = new Set(['customers', 'leads', 'attendees']);

// A list screen's rows open inside the page: the detail it shows and the route param that detail reads.
const DETAIL_SCREENS = {
  payments: { component: PaymentsChargesDetailOverview, param: 'chargeId' },
  subscriptions: { component: PaymentsSubscriptionsDetailOverview, param: 'subscriptionId' },
  bookings: { component: PaymentsBookingsDetailOverview, param: 'chargeId' },
  submissions: { component: SubmissionsDetailOverview, param: 'submissionId' },
  customers: { component: CustomerDetail, param: 'id' },
  leads: { component: CustomerDetail, param: 'id' },
  attendees: { component: CustomerDetail, param: 'id' },
};

// With group registration on, the event's list is of attendees, so a row opens the attendee.
const detailScreenFor = (path, page) =>
  path === 'attendees' && page.eventDetails?.attendeeDetails?.enabled
    ? { component: AttendeeDetail, param: 'ticketId' }
    : DETAIL_SCREENS[path];

// Add booking is a focused page of its own, routed in dashboard.js.
const getPageTabs = page =>
  getOverviewScreens(page)
    .filter(screen => screen.path !== 'add-booking')
    .map(screen => ({
      ...screen,
      component:
        SCREEN_COMPONENTS[page.type]?.[screen.path] ?? SCREEN_COMPONENTS.checkout[screen.path],
    }));

function PageOverview() {
  const { page, isLoading } = usePage();
  const topBarStyle = useTopBarStyle();
  const match = useRouteMatch();
  const recordMatch = useRouteMatch('/pages/:id/:screen/:recordId');
  const recordTabMatch = useRouteMatch('/pages/:id/:screen/:recordId/:tab');
  const screenMatch = useRouteMatch('/pages/:id/:screen');

  // Before the page loads, its last known shape draws the sidebar, so a refresh doesn't flash it.
  const navPage = !isLoading && page?._id ? page : readNavSnapshot(match.params.id);

  const pageTabs = getPageTabs(page);
  const screenPaths = pageTabs.map(tab => tab.path).join();
  const guestsAreAttendees = Boolean(page?.eventDetails?.attendeeDetails?.enabled);
  const pageScope = useMemo(
    () => ({
      pagePath: `/pages/${match.params.id}`,
      screens: screenPaths.split(','),
      guestsAreAttendees,
    }),
    [match.params.id, screenPaths, guestsAreAttendees],
  );

  // A record opened from a list spans the main area like the same record outside the page.
  const isRecord = Boolean(
    recordMatch &&
      DETAIL_SCREENS[recordMatch.params.screen] &&
      pageScope.screens.includes(recordMatch.params.screen),
  );
  // The page's type, and so its screens, is unknown until it loads, so the skeleton goes by the URL.
  const loadingScreen = screenMatch?.params.screen ?? 'overview';
  const loadingRecord = Boolean(recordMatch && DETAIL_SCREENS[recordMatch.params.screen]);
  // Sharing belongs with the page as a whole, not with its lists of orders and people.
  const showShare = screenMatch?.params.screen === 'overview';

  const shareButtonTitle =
    page.type === 'event' ? 'Share event' : page.type === 'form' ? 'Share form' : 'Share checkout';

  return (
    <div>
      <div className="relative">
        <nav
          className="flex h-14 border-b border-gray-200 dark:border-border px-4 items-center gap-x-4 fixed z-40 bg-white dark:bg-background transition-[left] duration-200 ease-linear"
          style={topBarStyle}
        >
          <EditorTopBarStart />

          <div className="hidden w-full max-w-xs md:block" style={PAGE_CONTENT_COLUMN_START}>
            <EditorSearchButton />
          </div>

          {showShare && (
            <div
              className="ml-auto flex flex-shrink-0 items-center space-x-3"
              style={PAGE_CONTENT_COLUMN_END}
            >
              {isLoading ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <CheckoutShareButton appearance="primary" checkoutId={match.params.id}>
                  <Share2Icon className="w-3.5 h-3.5 mr-1.5" />
                  {shareButtonTitle}
                </CheckoutShareButton>
              )}
            </div>
          )}
        </nav>
      </div>

      <div className="flex">
        {navPage && <EditorSidebar page={navPage} />}
        {!navPage && (
          <div
            aria-hidden="true"
            className="hidden md:block shrink-0 min-h-svh pr-4 border-r border-sidebar-border"
            style={{
              width: 'calc(var(--sidebar-offset, 0px) + var(--sidebar-width))',
              paddingLeft: 'calc(var(--sidebar-offset, 0px) + 1rem)',
            }}
          >
            <SummarySidebarSkeleton />
          </div>
        )}

        <div className="h-full min-w-0 flex-1 pt-14">
          {isLoading ? (
            loadingRecord ? (
              <div className="bg-white dark:bg-background pt-4">
                <div data-page-detail>
                  <div data-page-detail-back>
                    <Skeleton className="h-6 w-20" />
                  </div>
                  {CUSTOMER_SCREENS.has(loadingScreen) ? (
                    <OrderPageSkeleton tabs={4} variant="customer" />
                  ) : (
                    <OrderPageSkeleton
                      tabs={loadingScreen === 'bookings' ? 5 : 4}
                      {...orderTabSkeleton(recordTabMatch?.params.tab)}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-background px-4 pt-4">
                <div className="mx-auto max-w-[80rem]">
                  {loadingScreen === 'overview' ? <SummarySkeleton /> : <ListScreenSkeleton />}
                </div>
              </div>
            )
          ) : (
            <div
              className={`bg-white dark:bg-background h-full min-h-[calc(100vh_-_var(--top-banner-h,0px))] ${isRecord ? '' : 'px-4'} pt-4`}
            >
              <div className={isRecord ? undefined : 'mx-auto max-w-[80rem]'}>
                <PageScopeContext.Provider value={pageScope}>
                  <Switch>
                    <Redirect
                      exact
                      from={`/pages/${match.params.id}`}
                      to={`/pages/${match.params.id}/overview`}
                    />

                    {/* Bookmarked and help-center links to Analytics land on Summary, which has its charts. */}
                    <Redirect
                      from={`/pages/${match.params.id}/analytics`}
                      to={`/pages/${match.params.id}/overview`}
                    />

                    {/* The event's list was at /guests; saved links keep their record and filters. */}
                    {page.type === 'event' && (
                      <Route
                        path={`/pages/${match.params.id}/guests`}
                        render={({ location: from }) => (
                          <Redirect
                            to={{
                              pathname: from.pathname.replace(
                                `/pages/${match.params.id}/guests`,
                                `/pages/${match.params.id}/attendees`,
                              ),
                              search: from.search,
                            }}
                          />
                        )}
                      />
                    )}

                    {/* Staff is reached from Check-in in the sidebar, not from Overview. */}
                    {page.type === 'event' && (
                      <Route path={`/pages/${match.params.id}/staff`}>
                        <EventStaff page={page} />
                      </Route>
                    )}

                    {pageTabs
                      .filter(tab => DETAIL_SCREENS[tab.path])
                      .map(tab => {
                        const { component: Detail, param } = detailScreenFor(tab.path, page);
                        const listPath = `/pages/${match.params.id}/${tab.path}`;
                        return (
                          <Route
                            key={`${tab.path}-detail`}
                            path={`${listPath}/:${param}`}
                            render={props => (
                              <PageDetail
                                pagePath={`/pages/${match.params.id}`}
                                listPath={listPath}
                                listLabel={tab.label}
                              >
                                <Detail {...props} />
                              </PageDetail>
                            )}
                          />
                        );
                      })}

                    {pageTabs.map(tab => (
                      <Route path={`/pages/${match.params.id}/${tab.path}`} key={tab.path}>
                        {tab.path === 'overview' ? (
                          <PageSummaryRangeProvider page={page}>
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                              <h1 className="flex min-h-9 items-center text-lg font-semibold text-gray-900 dark:text-foreground">
                                {tab.label}
                              </h1>
                              <PageSummaryRangeControls />
                            </div>
                            <tab.component page={page} />
                          </PageSummaryRangeProvider>
                        ) : (
                          <DetailBaseContext.Provider
                            value={
                              DETAIL_SCREENS[tab.path]
                                ? `/pages/${match.params.id}/${tab.path}`
                                : null
                            }
                          >
                            <tab.component page={page} />
                          </DetailBaseContext.Provider>
                        )}
                      </Route>
                    ))}

                    <Redirect to={`/pages/${match.params.id}/overview`} />
                  </Switch>
                </PageScopeContext.Provider>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PageOverviewContainer = () => {
  const match = useRouteMatch();

  return (
    <PageProvider pageId={match.params.id}>
      <PageOverview />
    </PageProvider>
  );
};

export default PageOverviewContainer;
